import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CreateDirectJobInput,
  InterviewPlanOptions,
  JobConfig,
  JobChanges,
  JobConfigChangeResult,
  JobDetail,
  JobHeaderCounts,
  JobListRow,
  JobListScope,
  JobStart,
  OutList,
  OutOutcome,
  PipelineTemplateRequest,
  PipelineTemplateView,
  PublicFormDefinition,
  ReadyRequisition,
  RoleTemplateOption,
  RoleTemplateRequest,
  RoleTemplateView,
  StageBar,
  UpdateJobAssessmentInput,
  UpdateJobBasicsRequest,
  UpdateJobBriefInput,
  UpdateJobIdealProfileRequest,
  UpdateJobCriteriaRequest,
  UpdateJobFormRequest,
  UpdateJobInterviewPlanRequest,
  UpdateJobAssessmentCountInput,
  UpdateJobInterviewCountInput,
  PanelCandidate,
  AddGuestPanelistInput,
  UpdateJobMatrixRequest,
  UpdateJobOfferInput,
  UpdateJobPipelineInput,
} from '@nest-recruit/shared';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost, apiPut, request } from '../../../lib/api-client';

// S11: JOB-01 to JOB-05, ADMIN-01, ADMIN-05. S12: JOB-06 to JOB-09 (ADMIN-02 to ADMIN-04 are in evaluation-api.ts). `['jobs']` is the root key the API's
// realtime hint names, so a colleague's save refreshes this screen with no reload
// (non-negotiable #12). A job's creation also moves a requisition, so creating one
// refreshes `['requisitions']` too.

export const JOBS_KEY = ['jobs'] as const;
const REQUISITIONS_KEY = ['requisitions'] as const;
const ROLE_TEMPLATES_KEY = ['admin', 'role-templates'] as const;
const PIPELINES_KEY = ['admin', 'pipelines'] as const;

export function jobKey(id: string): readonly unknown[] {
  return [...JOBS_KEY, 'detail', id];
}

export interface JobListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly query?: string;
  readonly status?: string;
  readonly ventureId?: string;
  readonly departmentId?: string;
  /** "My jobs / All jobs" (R2). */
  readonly scope?: JobListScope;
}

export function useJobs(params: JobListParams) {
  return useQuery<{ rows: readonly JobListRow[]; total: number }, ApiError>({
    queryKey: [...JOBS_KEY, 'list', params],
    queryFn: async () => {
      const result = await request<readonly JobListRow[]>('/jobs', { query: { ...params } });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
  });
}

export function useJob(id: string): UseQueryResult<JobDetail, ApiError> {
  return useQuery<JobDetail, ApiError>({
    queryKey: jobKey(id),
    queryFn: () => apiGet<JobDetail>(`/jobs/${id}`),
  });
}

/**
 * ADR 0088 (JOB-12 ⋯ Changes). Under the job's key, so a save on the job (and the realtime hint for `['jobs']`) refreshes it; what
 * is on screen stays while it does.
 */
export function useJobChanges(id: string): UseQueryResult<JobChanges, ApiError> {
  return useQuery<JobChanges, ApiError>({
    queryKey: [...jobKey(id), 'changes'],
    queryFn: () => apiGet<JobChanges>(`/jobs/${id}/changes`),
    placeholderData: (previous) => previous,
  });
}

/**
 * JOB-12's stage bar (§L33). Under `['applications']`, so every move's realtime hint
 * recounts it; the previous counts stay on screen while it does (CLAUDE.md §5a rule 2).
 */
export function stageBarKey(jobId: string): readonly unknown[] {
  return ['applications', 'stage-bar', jobId];
}

export function useStageBar(jobId: string): UseQueryResult<StageBar, ApiError> {
  return useQuery<StageBar, ApiError>({
    queryKey: stageBarKey(jobId),
    queryFn: () => apiGet<StageBar>(`/jobs/${jobId}/stage-bar`),
    placeholderData: (previous) => previous,
  });
}

/**
 * Workflow review D1: the header's "Applied". Under `['applications']` like the stage bar, so a new applicant moves it
 * through the same realtime hint; the last number stays on screen while it recounts.
 */
export function useJobHeaderCounts(jobId: string, isEnabled: boolean): UseQueryResult<JobHeaderCounts, ApiError> {
  return useQuery<JobHeaderCounts, ApiError>({
    queryKey: ['applications', 'job-summary', jobId],
    queryFn: () => apiGet<JobHeaderCounts>(`/jobs/${jobId}/summary`),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

/** "Out: N" (§L33): the job's rejected, withdrawn and closed, a page at a time. */
export function useJobOut(jobId: string, page: number, outcome: OutOutcome | undefined, isEnabled: boolean): UseQueryResult<OutList, ApiError> {
  return useQuery<OutList, ApiError>({
    queryKey: ['applications', 'out', jobId, { page, outcome }],
    queryFn: () => apiGet<OutList>(`/jobs/${jobId}/out`, { query: { page, ...(outcome ? { outcome } : {}) } }),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

export function useReadyRequisitions(isEnabled: boolean): UseQueryResult<readonly ReadyRequisition[], ApiError> {
  return useQuery<readonly ReadyRequisition[], ApiError>({
    queryKey: [...JOBS_KEY, 'ready-requisitions'],
    queryFn: () => apiGet<readonly ReadyRequisition[]>('/jobs/ready-requisitions'),
    enabled: isEnabled,
  });
}

export function useJobStart(requisitionId: string): UseQueryResult<JobStart, ApiError> {
  return useQuery<JobStart, ApiError>({
    queryKey: [...JOBS_KEY, 'start', requisitionId],
    queryFn: () => apiGet<JobStart>(`/jobs/start/${requisitionId}`),
  });
}

export function useJobTemplates(id: string): UseQueryResult<readonly RoleTemplateOption[], ApiError> {
  return useQuery<readonly RoleTemplateOption[], ApiError>({
    queryKey: [...jobKey(id), 'templates'],
    queryFn: () => apiGet<readonly RoleTemplateOption[]>(`/jobs/${id}/templates`),
  });
}

export function useJobOwners(id: string): UseQueryResult<readonly { id: string; fullName: string }[], ApiError> {
  return useQuery<readonly { id: string; fullName: string }[], ApiError>({
    queryKey: [...jobKey(id), 'owners'],
    queryFn: () => apiGet<readonly { id: string; fullName: string }[]>(`/jobs/${id}/owners`),
  });
}

export function useFormDefinition(id: string, jobVersion: number): UseQueryResult<PublicFormDefinition, ApiError> {
  return useQuery<PublicFormDefinition, ApiError>({
    // Keyed on the job's version, so the preview follows every saved change.
    queryKey: [...jobKey(id), 'form-definition', jobVersion],
    queryFn: () => apiGet<PublicFormDefinition>(`/jobs/${id}/form-definition`),
    placeholderData: (previous) => previous,
  });
}

// --- Mutations ---------------------------------------------------------------------
//
// Each seeds the job with what the server returns — the whole job after the change —
// and then awaits the invalidation, so nothing on screen jumps back to the stale copy
// in between (CLAUDE.md §5a rule 3).

export function useCreateJob() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, { requisitionId: string; roleTemplateId: string | null }>({
    mutationFn: (input) => apiPost<{ id: string }>('/jobs', input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: JOBS_KEY }),
        queryClient.invalidateQueries({ queryKey: REQUISITIONS_KEY }),
      ]);
    },
  });
}

/** F5.24 (R1): a job HR creates directly, with no requisition. */
export function useCreateDirectJob() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, CreateDirectJobInput>({
    mutationFn: (input) => apiPost<{ id: string }>('/jobs/direct', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: JOBS_KEY });
    },
  });
}

/** One step's save. The caller owns the optimistic state; this seeds the cache. */
export function useSaveJobStep() {
  const queryClient = useQueryClient();
  return useMutation<
    JobDetail,
    ApiError,
    | { readonly id: string; readonly step: 'basics'; readonly body: UpdateJobBasicsRequest }
    | { readonly id: string; readonly step: 'criteria'; readonly body: UpdateJobCriteriaRequest }
    | { readonly id: string; readonly step: 'brief'; readonly body: UpdateJobBriefInput }
    // F5.31 (ADR 0092): the Ideal candidate profile's eight boxes, edited by HR.
    | { readonly id: string; readonly step: 'ideal-profile'; readonly body: UpdateJobIdealProfileRequest }
    | { readonly id: string; readonly step: 'matrix'; readonly body: UpdateJobMatrixRequest }
    | { readonly id: string; readonly step: 'form'; readonly body: UpdateJobFormRequest }
    | { readonly id: string; readonly step: 'pipeline'; readonly body: UpdateJobPipelineInput }
    | { readonly id: string; readonly step: 'assessment'; readonly body: UpdateJobAssessmentInput }
    | { readonly id: string; readonly step: 'interview-plan'; readonly body: UpdateJobInterviewPlanRequest }
    | { readonly id: string; readonly step: 'offer'; readonly body: UpdateJobOfferInput }
    // C4 (ADR 0077): the two counters.
    | { readonly id: string; readonly step: 'assessment-count'; readonly body: UpdateJobAssessmentCountInput }
    | { readonly id: string; readonly step: 'interview-count'; readonly body: UpdateJobInterviewCountInput }
  >({
    mutationFn: (input) =>
      input.step === 'basics'
        ? apiPatch<JobDetail>(`/jobs/${input.id}/basics`, input.body)
        : apiPut<JobDetail>(`/jobs/${input.id}/${input.step}`, input.body),
    onSuccess: async (job) => {
      queryClient.setQueryData(jobKey(job.id), job);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...JOBS_KEY, 'list'] }),
        queryClient.invalidateQueries({ queryKey: stageBarKey(job.id) }),
        // ADR 0088: a save may have changed what the requester wrote.
        queryClient.invalidateQueries({ queryKey: [...jobKey(job.id), 'changes'] }),
      ]);
    },
  });
}

/** F5.20 (R3): a file on a brief section, or the advert image. Returns the whole job after it. */
export async function attachJobFile(jobId: string, file: File, target: { readonly sectionKey: string } | { readonly isImage: true }): Promise<JobDetail> {
  const body = new FormData();
  body.append('file', file);
  const query = 'isImage' in target ? { as: 'image' } : { section: target.sectionKey };
  const result = await request<JobDetail>(`/jobs/${jobId}/files`, { method: 'POST', body, query });
  return result.data;
}

export async function removeJobFile(jobId: string, documentId: string): Promise<JobDetail> {
  return apiDelete<JobDetail>(`/jobs/${jobId}/files/${documentId}`);
}

/** Where a job file opens: the API streams it with the job's own permission check. */
export function jobFileUrl(jobId: string, documentId: string): string {
  return `/api/v1/jobs/${jobId}/files/${documentId}`;
}

/**
 * C6-a (ADR 0077): someone with no account, by name and email — returned as a person the panel picker
 * can seat at once. An address with an account returns that person instead.
 */
export function useAddGuestPanelist(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation<PanelCandidate, ApiError, AddGuestPanelistInput>({
    mutationFn: (input) => apiPost<PanelCandidate>(`/jobs/${jobId}/guest-panelists`, input),
    onSuccess: (person) => {
      // The picker shows them straight away; the next options fetch confirms it.
      queryClient.setQueryData<InterviewPlanOptions>([...jobKey(jobId), 'interview-options'], (options) =>
        options && !options.people.some((candidate) => candidate.id === person.id) ? { ...options, people: [...options.people, person] } : options,
      );
    },
  });
}

/** What step 6 chooses from. Kept on screen while a refetch runs (CLAUDE.md 5a rule 2). */
export function useInterviewOptions(id: string): UseQueryResult<InterviewPlanOptions, ApiError> {
  return useQuery<InterviewPlanOptions, ApiError>({
    queryKey: [...jobKey(id), 'interview-options'],
    queryFn: () => apiGet<InterviewPlanOptions>(`/jobs/${id}/interview-options`),
    placeholderData: (previous) => previous,
  });
}

/** F5.12. Seeds the opened job, so the page never flashes back to the draft. */
export function useOpenJob() {
  const queryClient = useQueryClient();
  return useMutation<JobDetail, ApiError, { id: string; version: number }>({
    mutationFn: ({ id, version }) => apiPost<JobDetail>(`/jobs/${id}/open`, { version }),
    onSuccess: async (job) => {
      queryClient.setQueryData(jobKey(job.id), job);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...JOBS_KEY, 'list'] }),
        queryClient.invalidateQueries({ queryKey: stageBarKey(job.id) }),
      ]);
    },
  });
}

/** F5.18. */
export function useSaveJobAsTemplate() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, { id: string; name: string }>({
    mutationFn: ({ id, name }) => apiPost<{ id: string }>(`/jobs/${id}/save-as-template`, { name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY });
    },
  });
}

export type ConfigChange =
  | { readonly kind: 'template'; readonly roleTemplateId: string }
  | { readonly kind: 'copy'; readonly fromJobId: string }
  | { readonly kind: 'restore'; readonly config: JobConfig };

/** F5.02, F5.03 and the undo behind their toast. Returns what was replaced. */
export function useChangeJobConfig() {
  const queryClient = useQueryClient();
  return useMutation<JobConfigChangeResult, ApiError, { id: string; version: number; change: ConfigChange }>({
    mutationFn: ({ id, version, change }) => {
      if (change.kind === 'template') {
        return apiPost(`/jobs/${id}/apply-template`, { version, roleTemplateId: change.roleTemplateId });
      }
      if (change.kind === 'copy') return apiPost(`/jobs/${id}/copy-setup`, { version, fromJobId: change.fromJobId });
      return apiPost(`/jobs/${id}/restore`, { version, config: change.config });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(jobKey(result.job.id), result.job);
      await queryClient.invalidateQueries({ queryKey: JOBS_KEY });
    },
  });
}

/** Workflow review C1 (ADR 0076): "Undo the copy", offered on the builder until the job opens. */
export function useUndoJobCopy() {
  const queryClient = useQueryClient();
  return useMutation<JobDetail, ApiError, { id: string; version: number }>({
    mutationFn: ({ id, version }) => apiPost<JobDetail>(`/jobs/${id}/undo-copy`, { version }),
    onSuccess: async (job) => {
      queryClient.setQueryData(jobKey(job.id), job);
      await queryClient.invalidateQueries({ queryKey: JOBS_KEY });
    },
  });
}

// --- ADMIN-05 ----------------------------------------------------------------------

/** `isEnabled`: only someone who may open the page asks (review W15: no refused request in the console). */
export function useRoleTemplates(isEnabled = true): UseQueryResult<readonly RoleTemplateView[], ApiError> {
  return useQuery<readonly RoleTemplateView[], ApiError>({
    queryKey: ROLE_TEMPLATES_KEY,
    queryFn: () => apiGet<readonly RoleTemplateView[]>('/admin/role-templates'),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

type TemplateRows = readonly RoleTemplateView[];

/**
 * Optimistic for an edit (CLAUDE.md §5a rule 1): the list shows the new name and switches at once, and goes back if the
 * server refuses. A new template joins the list with the server's answer, so the editor opened on it finds it.
 */
export function useSaveRoleTemplate() {
  const queryClient = useQueryClient();
  return useMutation<RoleTemplateView, ApiError, { id: string | null; version: number; body: RoleTemplateRequest }, { previous: TemplateRows | undefined }>({
    mutationFn: ({ id, version, body }) =>
      id
        ? apiPatch<RoleTemplateView>(`/admin/role-templates/${id}`, { ...body, version })
        : apiPost<RoleTemplateView>('/admin/role-templates', body),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: ROLE_TEMPLATES_KEY });
      const previous = queryClient.getQueryData<TemplateRows>(ROLE_TEMPLATES_KEY);
      if (id) {
        queryClient.setQueryData<TemplateRows>(ROLE_TEMPLATES_KEY, (rows) =>
          (rows ?? []).map((row) =>
            row.id === id
              ? { ...row, name: body.name, title: body.title, roleFamily: body.roleFamily ?? null, employmentType: body.employmentType, workMode: body.workMode, isActive: body.isActive ?? row.isActive }
              : row,
          ),
        );
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(ROLE_TEMPLATES_KEY, context.previous);
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData<TemplateRows>(ROLE_TEMPLATES_KEY, (rows) =>
        rows?.some((row) => row.id === saved.id) ? rows.map((row) => (row.id === saved.id ? saved : row)) : [...(rows ?? []), saved],
      );
      await queryClient.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY });
    },
  });
}

/**
 * F19.05. Optimistic: the decided template shows its new status (and a rejection its reason) at once and goes back only
 * if the server refuses (CLAUDE.md §5a rule 1).
 */
export function useDecideRoleTemplate() {
  const queryClient = useQueryClient();
  return useMutation<
    RoleTemplateView,
    ApiError,
    { id: string; version: number; decision: 'approve' | 'reject'; reason?: string },
    { previous: TemplateRows | undefined }
  >({
    mutationFn: ({ id, ...body }) => apiPost<RoleTemplateView>(`/admin/role-templates/${id}/decision`, body),
    onMutate: async ({ id, decision, reason }) => {
      await queryClient.cancelQueries({ queryKey: ROLE_TEMPLATES_KEY });
      const previous = queryClient.getQueryData<TemplateRows>(ROLE_TEMPLATES_KEY);
      queryClient.setQueryData<TemplateRows>(ROLE_TEMPLATES_KEY, (rows) =>
        (rows ?? []).map((row) =>
          row.id === id
            ? { ...row, status: decision === 'approve' ? 'approved' : 'rejected', decisionReason: decision === 'reject' ? (reason ?? null) : null }
            : row,
        ),
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(ROLE_TEMPLATES_KEY, context.previous);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY });
    },
  });
}

/**
 * F5.23 (R3): certify or uncertify. The badge moves at once and goes back if refused. The version sent is the one cached
 * when the request goes, so the toast's Undo after another change still finds the template (review W16).
 */
export function useCertifyRoleTemplate() {
  const queryClient = useQueryClient();
  return useMutation<RoleTemplateView, ApiError, { id: string; isCertified: boolean }, { previous: TemplateRows | undefined }>({
    mutationFn: ({ id, isCertified }) => {
      const version = queryClient.getQueryData<TemplateRows>(ROLE_TEMPLATES_KEY)?.find((row) => row.id === id)?.version ?? 0;
      return apiPost<RoleTemplateView>(`/admin/role-templates/${id}/${isCertified ? 'certify' : 'uncertify'}`, { version });
    },
    onMutate: async ({ id, isCertified }) => {
      await queryClient.cancelQueries({ queryKey: ROLE_TEMPLATES_KEY });
      const previous = queryClient.getQueryData<TemplateRows>(ROLE_TEMPLATES_KEY);
      queryClient.setQueryData<TemplateRows>(ROLE_TEMPLATES_KEY, (rows) =>
        (rows ?? []).map((row) => (row.id === id ? { ...row, isCertified } : row)),
      );
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(ROLE_TEMPLATES_KEY, context.previous);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData<TemplateRows>(ROLE_TEMPLATES_KEY, (rows) => (rows ?? []).map((row) => (row.id === saved.id ? saved : row)));
    },
    onSettled: async () => {
      // Templates pickers on jobs list certified first, so they refresh too.
      await Promise.all([queryClient.invalidateQueries({ queryKey: ROLE_TEMPLATES_KEY }), queryClient.invalidateQueries({ queryKey: JOBS_KEY })]);
    },
  });
}

// --- ADMIN-01 ----------------------------------------------------------------------

/** `isEnabled`: only someone who may open the page asks (review W15). */
export function usePipelineTemplates(isEnabled = true): UseQueryResult<readonly PipelineTemplateView[], ApiError> {
  return useQuery<readonly PipelineTemplateView[], ApiError>({
    queryKey: PIPELINES_KEY,
    queryFn: () => apiGet<readonly PipelineTemplateView[]>('/admin/pipelines'),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

type PipelineRows = readonly PipelineTemplateView[];

/**
 * Optimistic for an edit: the list shows the new name, route and default at once (one default, as the server keeps it),
 * and goes back if the server refuses. The answer is the whole list.
 */
export function useSavePipelineTemplate() {
  const queryClient = useQueryClient();
  return useMutation<PipelineRows, ApiError, { id: string | null; version: number; body: PipelineTemplateRequest }, { previous: PipelineRows | undefined }>({
    mutationFn: ({ id, version, body }) =>
      id
        ? apiPatch<PipelineRows>(`/admin/pipelines/${id}`, { ...body, version })
        : apiPost<PipelineRows>('/admin/pipelines', body),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: PIPELINES_KEY });
      const previous = queryClient.getQueryData<PipelineRows>(PIPELINES_KEY);
      if (id) {
        const isDefault = body.isDefault ?? false;
        queryClient.setQueryData<PipelineRows>(PIPELINES_KEY, (rows) =>
          (rows ?? []).map((row) => {
            if (row.id === id) return { ...row, name: body.name, roleFamily: body.roleFamily ?? null, isDefault, pipeline: body.pipeline };
            return isDefault ? { ...row, isDefault: false } : row;
          }),
        );
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(PIPELINES_KEY, context.previous);
    },
    onSuccess: (pipelines) => {
      queryClient.setQueryData(PIPELINES_KEY, pipelines);
    },
  });
}
